import {
  Habit,
  RepeatingTask,
  ScheduleEntryState,
  Task,
  TaskActionType,
  UpdateTask,
} from '@griply/types';
import { isSameDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle216 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import {
  getScheduleByDate,
  updateScheduleEntryForDate,
} from 'shared/utils/schedules';

import { MenuGroups } from './types';

export type CompleteListProps = {
  tasks: Task[];
  onClose: () => void;
  registerItem?: (
    group: MenuGroups,
    index: number,
    element: HTMLButtonElement | null,
  ) => void;
  registerGroup?: MenuGroups;
};

export const CompleteList: React.FC<CompleteListProps> = ({
  tasks,
  onClose,
  registerItem,
  registerGroup,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const track = useTrackEvents();

  const onComplete = useCallback(() => {
    const tasksToUpdate: UpdateTask[] = [];
    const goalUpdates: Array<{ goalId: string; completedTaskCount: number }> =
      [];

    tasks.forEach((task) => {
      // habit and repeating tasks
      if (task.schedules?.length) {
        // This allows us to batch all updates together instead of calling updateHabitEntry separately
        const habit = task as Habit | RepeatingTask;

        // Calculate completion value based on schedule
        const dateSchedule = getScheduleByDate(habit.schedules, {
          date: today,
          today,
        });
        const frequencyTarget = dateSchedule
          ? getScheduleFrequencyDayTarget(dateSchedule) ??
            dateSchedule.frequency?.count ??
            1
          : 1;

        const completionValue = dateSchedule
          ? dateSchedule?.frequency
            ? frequencyTarget
            : dateSchedule?.completionTarget.count
          : 1;

        // Check if task is already completed for today
        const entry = dateSchedule?.entries.find((entry) =>
          isSameDay(entry.date, today),
        );
        const isCompleted =
          entry?.state === ScheduleEntryState.Complete &&
          (dateSchedule?.frequency
            ? (entry.value ?? 0) >= frequencyTarget
            : (entry.value ?? 0) >=
              (dateSchedule?.completionTarget.count ?? 1));

        const value = isCompleted ? 0 : completionValue;

        // Update schedules using the helper function
        const newSchedules = updateScheduleEntryForDate(habit, today, {
          type: ScheduleEntryState.Complete,
          today,
          value,
        });

        // Track analytics event (only when completing, not uncompleting)
        // Check if completion threshold was reached
        if (!isCompleted && dateSchedule) {
          const shouldTrack = dateSchedule.frequency
            ? value >=
              (getScheduleFrequencyDayTarget(dateSchedule) ??
                dateSchedule.frequency.count)
            : value >= dateSchedule.completionTarget.count;

          if (shouldTrack) {
            track(ActionEvents.TaskComplete, {
              type: taskTypeToTrackingTaskType[habit.type],
            });
          }
        }

        // Add updated habit to tasksToUpdate
        tasksToUpdate.push({
          id: habit.id,
          schedules: newSchedules,
        });

        // Uncomplete child tasks (if any)
        if (habit.childIds?.length) {
          habit.childIds.forEach((childId) => {
            tasksToUpdate.push({
              id: childId,
              completedAt: null,
            });
          });
        }
        return;
      }

      // regular tasks
      // Extract completion logic from useCompleteTask
      // Toggle completedAt: set to today if not completed, null if already completed
      const taskToUpdate = structuredClone(task);
      const wasCompleted = !!taskToUpdate.completedAt;
      taskToUpdate.completedAt = wasCompleted ? null : today;

      tasksToUpdate.push(taskToUpdate);

      // Track analytics (only when completing, not uncompleting)
      if (taskToUpdate.completedAt) {
        track(ActionEvents.TaskComplete, {
          type: taskTypeToTrackingTaskType[taskToUpdate.type],
        });
      }

      if (
        !taskToUpdate.goalId ||
        taskToUpdate.action?.type === TaskActionType.GoalTimeslot
      ) {
        return;
      }

      // Track goal updates
      const existingUpdate = goalUpdates.find(
        (g) => g.goalId === taskToUpdate.goalId,
      );
      if (existingUpdate) {
        existingUpdate.completedTaskCount += taskToUpdate.completedAt ? 1 : -1;
        return;
      }
      goalUpdates.push({
        goalId: taskToUpdate.goalId,
        completedTaskCount: taskToUpdate.completedAt ? 1 : -1,
      });
    });

    // Batch update all tasks (both regular tasks and habits with schedules)
    if (tasksToUpdate.length) {
      updateTasks(tasksToUpdate);
    }

    // Batch update goal task counts
    goalUpdates.forEach((update) => {
      updateGoalTaskCount(update);
    });

    onClose();
  }, [tasks, today, track, updateTasks, updateGoalTaskCount, onClose]);

  return (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onComplete}
          start={<Icon icon={CheckCircle216} />}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 0, el)}
        >
          {t('context-menus.task.labels.complete')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
